import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMachinePhase } from '@atlassian/malleable-ui-components/state/machine';
import type { EditorRemixLifecycleEvent } from '@atlassian/malleable-ui-components/types';

import type {
	EditorInsertionAcknowledgement,
	EditorInsertionNotAcknowledgedResult,
	SmartCreationModalCloseResult,
} from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import {
	useObjectSidebarRemixLifecycleDiagnostics,
	type ObjectSidebarStagingTransferCallbackName,
} from './useObjectSidebarRemixLifecycleDiagnostics.ts';

export type ObjectSidebarStagingTransferTerminal =
	| 'handed-off'
	| 'inserted'
	| 'cancelled'
	| 'abandoned';

export type ObjectSidebarStagingTransferLifecycle = {
	remixId: string;
	conversationId: string;
	transition: (event: EditorRemixLifecycleEvent) => boolean;
	isCurrent: () => boolean;
};

export type ObjectSidebarStagingTransfer = {
	lifecycle: ObjectSidebarStagingTransferLifecycle;
	settle: (terminal: ObjectSidebarStagingTransferTerminal) => void;
};

/** Identifies one transfer. Callbacks bound to it are ignored once another transfer is pending. */
export type ObjectSidebarStagingTransferToken = symbol;

/**
 * Callbacks bound to a single transfer, shaped so the container can spread them straight into
 * `openAIFCStagingModal`. A callback captured by an earlier modal (or by the editor's deferred
 * insertion acknowledgement) can therefore never act on a later transfer.
 */
export type ObjectSidebarStagingTransferCallbacks = {
	onPreviewOpened: () => void;
	onSubmitted: () => void;
	onPreviewDismissed: (result?: SmartCreationModalCloseResult) => void;
	/** Both acknowledgement branches are always bound, unlike the store's optional `onNotAcknowledged`. */
	editorInsertionAcknowledgement: Required<EditorInsertionAcknowledgement>;
};

export type ObjectSidebarStagingTransferController = {
	begin: (transfer: ObjectSidebarStagingTransfer) => ObjectSidebarStagingTransferToken;
	callbacksFor: (token: ObjectSidebarStagingTransferToken) => ObjectSidebarStagingTransferCallbacks;
	isPending: () => boolean;
};

type PendingTransfer = ObjectSidebarStagingTransfer & {
	token: ObjectSidebarStagingTransferToken;
	previewOpened: boolean;
	previewOpenQueued: boolean;
	accepted: boolean;
};

type SettledTransfer = {
	token: ObjectSidebarStagingTransferToken;
	terminal: ObjectSidebarStagingTransferTerminal;
};

/**
 * Owns the Object Sidebar lifecycle while a generated object (slides, whiteboard, database) sits
 * in the Smart Creation staging preview, and settles it exactly once from the destination's
 * acknowledgement (CCCEMAU-3624).
 */
export const useObjectSidebarStagingTransfer = ({
	hidePopup,
}: {
	hidePopup: () => void;
}): ObjectSidebarStagingTransferController => {
	const report = useObjectSidebarRemixLifecycleDiagnostics();
	const pendingRef = useRef<PendingTransfer | undefined>(undefined);
	const lastSettledRef = useRef<SettledTransfer | undefined>(undefined);
	const [activeRemixId, setActiveRemixId] = useState<string | undefined>(undefined);
	const phase = useMachinePhase(activeRemixId ?? '');

	const clear = useCallback(() => {
		pendingRef.current = undefined;
		setActiveRemixId(undefined);
	}, []);

	/** The pending transfer, or undefined once the owner no longer holds its lifecycle. */
	const current = useCallback((): PendingTransfer | undefined => {
		const pending = pendingRef.current;
		if (!pending) {
			return undefined;
		}
		if (!pending.lifecycle.isCurrent()) {
			clear();
			return undefined;
		}
		return pending;
	}, [clear]);

	/**
	 * The pending transfer only when `token` identifies it. Anything else is a callback from a
	 * transfer that already settled or was superseded: report it and let the caller no-op. The one
	 * exception is a callback for the transfer that settled most recently, which the caller may
	 * still want to record as a post-settlement acknowledgement.
	 */
	const currentFor = useCallback(
		(
			token: ObjectSidebarStagingTransferToken,
			callback: ObjectSidebarStagingTransferCallbackName,
		): PendingTransfer | undefined => {
			const pending = current();
			if (pending?.token === token) {
				return pending;
			}
			if (pending || lastSettledRef.current?.token !== token) {
				report('observed', { operation: 'staleStagingTransferCallback', callback });
			}
			return undefined;
		},
		[current, report],
	);

	const settle = useCallback(
		(pending: PendingTransfer, terminal: ObjectSidebarStagingTransferTerminal) => {
			clear();
			lastSettledRef.current = { token: pending.token, terminal };
			report('observed', { operation: 'stagingTransferSettled', outcome: terminal });
			pending.settle(terminal);
		},
		[clear, report],
	);

	const begin = useCallback(
		(transfer: ObjectSidebarStagingTransfer): ObjectSidebarStagingTransferToken => {
			const previous = pendingRef.current;
			if (previous) {
				if (previous.lifecycle.isCurrent()) {
					// Single-active admission means a new flow cannot start while the previous one is
					// still owned, so this should be unreachable. Surface it rather than settling the
					// previous transfer through a close handler that may already belong to the new flow.
					report('observed', { operation: 'stagingTransferSuperseded' });
				}
				clear();
			}
			const token: ObjectSidebarStagingTransferToken = Symbol('object-sidebar-staging-transfer');
			pendingRef.current = {
				...transfer,
				token,
				previewOpened: false,
				previewOpenQueued: false,
				accepted: false,
			};
			setActiveRemixId(transfer.lifecycle.remixId);
			hidePopup();
			return token;
		},
		[clear, hidePopup, report],
	);

	const sendPreviewOpened = useCallback((pending: PendingTransfer) => {
		if (pending.previewOpened) {
			return;
		}
		if (pending.lifecycle.transition({ type: 'PREVIEW_OPENED' })) {
			pending.previewOpened = true;
			pending.previewOpenQueued = false;
			return;
		}
		// PREVIEW_OPENED is legal only from `ready`; the preview can open before the watcher
		// reports RESULT_READY. Retry when the phase changes.
		pending.previewOpenQueued = true;
	}, []);

	useEffect(() => {
		// Read the ref directly: `current()` may call `clear()`, and a state update inside an effect
		// trips `@atlassian/perf-linting/no-chain-state-updates`. A stale transfer is dropped lazily
		// by the next callback instead.
		const pending = pendingRef.current;
		if (pending?.previewOpenQueued && phase === 'ready' && pending.lifecycle.isCurrent()) {
			sendPreviewOpened(pending);
		}
	}, [phase, sendPreviewOpened]);

	const previewOpenedFor = useCallback(
		(token: ObjectSidebarStagingTransferToken) => {
			const pending = currentFor(token, 'previewOpened');
			if (pending) {
				sendPreviewOpened(pending);
			}
		},
		[currentFor, sendPreviewOpened],
	);

	const accept = useCallback(
		(pending: PendingTransfer) => {
			if (pending.accepted) {
				return;
			}
			if (pending.lifecycle.transition({ type: 'ACCEPTED' })) {
				pending.accepted = true;
			} else {
				report('observed', { operation: 'acceptedRejected' });
			}
		},
		[report],
	);

	const submittedFor = useCallback(
		(token: ObjectSidebarStagingTransferToken) => {
			const pending = currentFor(token, 'submitted');
			if (!pending) {
				return;
			}
			accept(pending);
			settle(pending, 'handed-off');
		},
		[accept, currentFor, settle],
	);

	const insertionAcknowledgedFor = useCallback(
		(token: ObjectSidebarStagingTransferToken) => {
			const pending = currentFor(token, 'insertionAcknowledged');
			if (!pending) {
				// Submit already settled this transfer (Task 0 precedence rule); keep the over-report visible.
				if (lastSettledRef.current?.token === token) {
					report('observed', {
						operation: 'insertionAcknowledgementAfterSettlement',
						outcome: 'inserted',
					});
				}
				return;
			}
			accept(pending);
			if (pending.accepted) {
				if (pending.lifecycle.transition({ type: 'INSERTED' })) {
					settle(pending, 'inserted');
					return;
				}
				// The destination confirmed the insertion even though the machine would not record it.
				report('observed', { operation: 'insertedRejected' });
			}
			// A refused ACCEPTED was already reported by accept(); INSERTED was never attempted.
			settle(pending, 'handed-off');
		},
		[accept, currentFor, report, settle],
	);

	const insertionNotAcknowledgedFor = useCallback(
		(token: ObjectSidebarStagingTransferToken, result: EditorInsertionNotAcknowledgedResult) => {
			const pending = currentFor(token, 'insertionNotAcknowledged');
			if (!pending) {
				if (lastSettledRef.current?.token === token) {
					report('observed', {
						operation: 'insertionAcknowledgementAfterSettlement',
						outcome: result.outcome,
					});
				}
				return;
			}
			report('observed', { operation: 'insertionNotAcknowledged', outcome: result.outcome });
			if (result.outcome === 'timed-out-in-flight') {
				// Delivered but unproven: release rather than hold a possible duplicate (3486).
				settle(pending, 'abandoned');
			}
			// Otherwise the recovery UI is still open and the flow stays owned.
		},
		[currentFor, report, settle],
	);

	const previewDismissedFor = useCallback(
		(token: ObjectSidebarStagingTransferToken, result?: SmartCreationModalCloseResult) => {
			const pending = currentFor(token, 'previewDismissed');
			if (!pending) {
				return;
			}
			if (result?.continuedInRovoChat) {
				// The full modal's close and minimize both open Rovo Chat on the conversation, so the
				// generation carries on there. That is the Rovo Chat handoff (CCCEMAU-3623 acknowledgement:
				// conversation opened): hand off with no machine terminal event, never a cancellation.
				settle(pending, 'handed-off');
				return;
			}
			pending.lifecycle.transition({ type: 'CANCELLED' });
			settle(pending, 'cancelled');
		},
		[currentFor, settle],
	);

	const callbacksFor = useCallback(
		(token: ObjectSidebarStagingTransferToken): ObjectSidebarStagingTransferCallbacks => ({
			onPreviewOpened: () => previewOpenedFor(token),
			onSubmitted: () => submittedFor(token),
			onPreviewDismissed: (result) => previewDismissedFor(token, result),
			editorInsertionAcknowledgement: {
				onAcknowledged: () => insertionAcknowledgedFor(token),
				onNotAcknowledged: (result) => insertionNotAcknowledgedFor(token, result),
			},
		}),
		[
			insertionAcknowledgedFor,
			insertionNotAcknowledgedFor,
			previewDismissedFor,
			previewOpenedFor,
			submittedFor,
		],
	);

	const isPending = useCallback(() => current() !== undefined, [current]);

	// Stable identity: consumers put this object in dependency arrays.
	return useMemo(() => ({ begin, callbacksFor, isPending }), [begin, callbacksFor, isPending]);
};
